import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { PersonRecord } from "../types/data";
import {
  convertInCurrentLang,
  convertNumberInCurrentLang,
  convertWeight
} from "../utils/misc";
import { useUserData } from "../contexts/UserContext";
import MealLog from "../cards/MealLog";
import NutrientMeter from "../cards/NutrientMeter";
import { fishDatabase } from "../data/fish_db";
import { motion } from "framer-motion";
import RespondentDetailCard from "../cards/RespondentDetails";
import { storageService } from "../services/storage.service";
import { ModalWindow } from "../components/ModalWindow";

export default function LogHistoryDateLog() {
  const { t, lang } = useLang();
  const { date, personId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, getPerson, updatePerson } = useUserData();
  const [modalChildren, setModalChildren] = useState<React.ReactNode>(null);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const person: PersonRecord | null = getPerson(personId || "") || null;
  const dayRecord = person?.records?.find((r) => r.date === date) || null;
  
  // Track whether "no fish consumed" is checked
  const [noFishConsumed, setNoFishConsumed] = useState(dayRecord?.no_fish_consumed || false);
  // Track if we're currently saving
  const [isSaving, setIsSaving] = useState(false);
  const [showNutrientOutcome, setShowNutrientOutcome] = useState(true);
  const [showNutrientDRI, setShowNutrientDRI] = useState(false);

  useEffect(() => {
    storageService.getSettings().then(settings => {
      setShowNutrientOutcome(settings.showNutrientOutcome ?? true);
      setShowNutrientDRI(settings.showNutrientDRI ?? false);
    }).catch(() => {});
  }, []);

  // Determine where user came from and what the back button should do/say
  const backInfo = useMemo(() => {
    const from = searchParams.get('from');
    const calendarFrom = searchParams.get('calendar_from');
    const state = location.state as { from?: string } | undefined;
    const fromPath = state?.from;
    
    // Priority 1: Check if came directly from all_logs (has from=all_logs but NOT calendar_from)
    if (from === 'all_logs' && !calendarFrom) {
      return { text: 'logHistoryDateLog_BackToAllLogs', action: () => navigate('/survey/all-logs') };
    }
    
    // Priority 2: Check if came from calendar that came from somewhere (has calendar_from)
    if (calendarFrom === 'all_logs') {
      return { 
        text: 'logHistoryDateLog_BackToCalendar', 
        action: () => navigate(`/log-history/${personId}/?from=all_logs`) 
      };
    }
    
    // Priority 3: Check location.state for special cases (e.g., coming from survey recent logs)
    if (fromPath) {
      if (fromPath.includes('/survey/all-logs')) {
        return { text: 'logHistoryDateLog_BackToAllLogs', action: () => navigate('/survey/all-logs') };
      }
      if (fromPath.includes('/survey')) {
        return { text: 'back', action: () => navigate('/survey') };
      }
    }
    
    // Default: always go back to calendar view (no special origin)
    return { 
      text: 'logHistoryDateLog_BackToCalendar', 
      action: () => navigate(`/log-history/${personId}/`) 
    };
  }, [searchParams, location.state, navigate, personId]);

  //console.log("LogHistoryDateLog", { person, date, dayRecord });

  if (!person) {
    return (
      <div className="text-center mt-16 text-red-600">
        {t("logHistoryDateLog_NoPerson")}
      </div>
    );
  }

  const weight_unit = user?.appData?.weight_unit || "gm";

  // Filter meals that have fish items
  let mealsWithFish =
    dayRecord?.meals.filter((meal) => meal.fish_items.length > 0) || [];

  const mergeDuplicateMeals = (meals: any[]) => {
    const merged: any[] = [];
    const mealMap: Record<string, any> = {};
    meals.forEach((meal) => {
      const key = meal.meal_type;
      if (!mealMap[key]) {
        mealMap[key] = { ...meal, fish_items: [...meal.fish_items] };
        merged.push(mealMap[key]);
      } else {
        mealMap[key].fish_items.push(...meal.fish_items);
        if (
          meal.updated_at &&
          mealMap[key].updated_at &&
          meal.updated_at > mealMap[key].updated_at
        ) {
          mealMap[key].updated_at = meal.updated_at;
        }
      }
    });
    return merged;
  };

  if (mealsWithFish.length > 0) {
    mealsWithFish = mergeDuplicateMeals(mealsWithFish);
    
    // Sort meals by meal type order: breakfast -> lunch -> snack -> dinner
    const mealOrder = { breakfast: 1, lunch: 2, snack: 3, dinner: 4 };
    mealsWithFish.sort((a, b) => {
      const orderA = mealOrder[a.meal_type as keyof typeof mealOrder] || 999;
      const orderB = mealOrder[b.meal_type as keyof typeof mealOrder] || 999;
      return orderA - orderB;
    });
  }

  interface TotalConsumption {
    fishId: string;
    amount_in_gm: number;
  }

  const totalConsumptions = (): TotalConsumption[] => {
    const total: Record<string, number> = {};
    mealsWithFish.forEach((meal) => {
      meal.fish_items.forEach((item) => {
        if (item.fish_id && item.estimated_weight) {
          if (!total[item.fish_id]) {
            total[item.fish_id] = 0;
          }
          total[item.fish_id] += convertWeight(
            item.estimated_weight,
            item.weight_unit || "gm",
            "gm"
          );
        }
      });
    });

    return Object.entries(total).map(([fishId, amount_in_gm]) => ({
      fishId,
      amount_in_gm,
    }));
  };

  const numberOfConsumedItems = mealsWithFish.reduce(
    (acc, meal) => acc + meal.fish_items.length,
    0
  );

  // "Other" fish (fish_id "other") is not in the fish database and has no
  // nutrient data, so hide nutrient sections when no known fish exists.
  const hasAnyKnownFish = mealsWithFish.some((meal) =>
    meal.fish_items.some((item) => fishDatabase.some((f) => f.id === item.fish_id))
  );

  const consumedFishWeight = mealsWithFish.reduce(
    (acc, meal) =>
      acc +
      meal.fish_items.reduce(
        (sum, item) =>
          sum +
          convertWeight(
            item.estimated_weight || 0,
            item.weight_unit || "gm",
            weight_unit
          ),
        0
      ),
    0
  );

  let totalConsumption = null;
  if (numberOfConsumedItems > 1) {
    totalConsumption = totalConsumptions();
  }

  // console.log("Total Consumption:", numberOfConsumedItems, totalConsumption);

  const addNewMealRecord = () => {
    if (user?.id === person.id) {
      navigate(`/user/new-log/${date}?ref=date_log`);
    } else {
      navigate(`/survey/new/${person.id}/${date}?ref=date_log`);
    }
  };

  const deleteFishItem = (itemId: string) => {
    if (!dayRecord || !dayRecord.meals || dayRecord.meals.length === 0) return;

    const confirmed = window.confirm(
      t("logHistoryDateLog_DeleteFishItemConfirm")
    );

    if (!confirmed) return;

    let updatedMeals = dayRecord.meals.map((meal) => {
      const newFishItems = meal.fish_items.filter((item) => item.id !== itemId);
      return { ...meal, fish_items: newFishItems };
    });

    // remove empty meals
    updatedMeals = updatedMeals.filter((meal) => meal.fish_items.length > 0);

    if (updatedMeals.length === 0) {
      // Check if this day has no_fish_consumed flag - if so, preserve the record
      if (dayRecord.no_fish_consumed) {
        // Keep the day record with no meals but no_fish_consumed=true
        updatePerson({
          ...person,
          records: person.records.map((record) =>
            record.date === date
              ? {
                  ...record,
                  meals: [],
                  no_fish_consumed: true,
                  updated_at: new Date().toISOString(),
                }
              : record
          ),
          updated_at: new Date().toISOString()
        });

        window.alert(
          t("logHistoryDateLog_FishItemRemoved") ||
            "The fish item has been removed."
        );

        navigate(0); // Refresh the page
        return;
      }
      
      // If no meals left and no no_fish_consumed flag, remove the day record
      const updatedRecords = person.records.filter(
        (record) => record.date !== date
      );
      updatePerson({ ...person, records: updatedRecords, updated_at: new Date().toISOString() });

      window.alert(
        t("logHistoryDateLog_NoMealsLeft") ||
          "No meals left for this day. The day record has been removed."
      );

      navigate(-1); // Go back to the previous page
      return;
    }

    updatePerson({
      ...person,
      records: person.records.map((record) =>
        record.date === date
          ? {
              ...record,
              meals: updatedMeals,
              updated_at: new Date().toISOString(),
            }
          : record
      ),
    });

    window.alert(
      t("logHistoryDateLog_FishItemRemoved") ||
        "The fish item has been removed."
    );

    navigate(0); // Refresh the page to reflect changes
  };

  const toggleNoFishConsumed = () => {
    const newValue = !noFishConsumed;
    setNoFishConsumed(newValue);
    setIsSaving(true);

    if (newValue) {
      // Create or update day record with no_fish_consumed=true
      const existingDayRecord = person.records.find(r => r.date === date);
      
      if (existingDayRecord) {
        // Update existing record
        updatePerson({
          ...person,
          records: person.records.map(record =>
            record.date === date
              ? { ...record, no_fish_consumed: true, updated_at: new Date().toISOString() }
              : record
          ),
          updated_at: new Date().toISOString()
        });
      } else {
        // Create new day record
        const newDayRecord = {
          date: date!,
          surveyed_on: new Date().toISOString(),
          meals: [],
          no_fish_consumed: true
        };
        updatePerson({
          ...person,
          records: [...person.records, newDayRecord],
          updated_at: new Date().toISOString()
        });
      }
    } else {
      // Unchecked: remove no_fish_consumed flag
      // If day has no meals, delete the day record entirely to avoid empty records
      const existingDayRecord = person.records.find(r => r.date === date);
      
      if (existingDayRecord && (!existingDayRecord.meals || existingDayRecord.meals.length === 0)) {
        // No meals present, delete the entire day record
        updatePerson({
          ...person,
          records: person.records.filter(record => record.date !== date),
          updated_at: new Date().toISOString()
        });
      } else {
        // Has meals, just remove the no_fish_consumed flag
        updatePerson({
          ...person,
          records: person.records.map(record =>
            record.date === date
              ? { ...record, no_fish_consumed: false, updated_at: new Date().toISOString() }
              : record
          ),
          updated_at: new Date().toISOString()
        });
      }
    }

    // Wait 2 seconds for database commit before allowing navigation
    setTimeout(() => {
      setIsSaving(false);
    }, 2000);
  };

  const handlePersonNameClick = () => {
    if (!person) return;
    const modalChildren = (
      <div className="bg-white bg-opacity-75 backdrop-blur-md rounded-3xl shadow-lg mx-scr p-4">
        <div className="flex flex-col gap-4 ">
          <RespondentDetailCard person={person} />        
        </div>
      </div>
    );

    setModalChildren(modalChildren);
    //window.alert(modalChildren)
  };

  const modalCloseHandler = () => {
    setModalChildren(null);
  };

  return (
    <motion.div className="flex flex-col grow"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}>
      <div className="relative w-full grow flex flex-col items-center justify-start">
        <div className="w-full flex flex-col items-center justify-start gap-6 text-center px-scr pt-8 grow">
          <h1 className="text-2xl font-bold text-rose-700">
            {/* {t("logHistory_Heading").replace("{name}", person.name || "")} */}
             {lang === "bn" ? (
              <span>
                <a
                  onClick={handlePersonNameClick}
                  className="font-bold cursor-pointer"
                >
                  {person.name || "---"}{" "}
                </a> 
                <span>এর মাছ খাওয়ার লগ</span>
              </span>
            ) : (
              <span>
                <span>Log History of </span>
                <a
                  onClick={handlePersonNameClick}
                  className="font-bold cursor-pointer"
                > {person.name || "---"}</a>
              </span>
            )}
          </h1>

          <div className="bg-gray-100/50 backdrop-blur rounded-3xl w-full p-4 text-center text-lg">
            <p className="text-xl font-bold text-blue-700">
              {t("logHistoryDateLog_Heading").replace(
                "{date}",
                convertInCurrentLang(lang, date || "")
              )}
            </p>

            {numberOfConsumedItems > 1 && hasAnyKnownFish && showNutrientOutcome && (
              <div className="flex flex-col items-center justify-center mt-4 border-t pt-2">
                <h1 className="text-lg font-bold text-green-800">
                  {t("logHistoryDateLog_totalNutrientIntakeHeader")}
                </h1>
                <h1 className="text-sm text-gray-600">
                  {t("logHistoryDateLog_totalNutrientIntakeSubheader")}
                </h1>
                <table className="w-full fish-log-table mt-2">
                  <colgroup>
                    <col style={{ width: "50%" }} />
                    <col style={{ width: "50%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td colSpan={2}>
                        <div className="flex flex-col gap-2">
                          <h4 className="text-sm font-semibold text-pink-700 mt-1">
                            {t("logHistoryDateLog_totalConsumedWeightHeader")}:{" "}
                            {convertNumberInCurrentLang(lang, consumedFishWeight)}{" "}
                            {weight_unit === "oz"
                              ? t("unit_oz")
                              : t("unit_gm")}
                          </h4>
                          <NutrientMeter fishes={totalConsumption || []} showDRI={showNutrientDRI} />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {mealsWithFish.length > 0 ? (
              mealsWithFish.map((meal, mealIdx) => (
                <MealLog
                  meal={meal}
                  key={mealIdx}
                  allowEdit={false}
                  allowDelete={true}
                  onDelete={(item) => deleteFishItem(item.id)}
                />
              ))
            ) : (
              <div className="border-t pt-2 mt-2">  
                {!noFishConsumed && (
                  <p className="text-rose-700 text-center mt-2">
                    {t("logHistoryDateLog_NoRecord")}
                  </p>
                )}


                {/* No Fish Consumed Toggle */}
                <div className="flex justify-center mt-6">
                  <label 
                    htmlFor="no-fish-toggle" 
                    className="relative cursor-pointer select-none group"
                  >
                    <div 
                      className={`
                        relative overflow-hidden
                        pl-2 pr-4 py-2 rounded-full
                        border-2 transition-all duration-300
                        ${noFishConsumed 
                          ? 'border-rose-500 shadow-lg shadow-rose-200' 
                          : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                        }
                      `}
                    >
                      {/* Animated background */}
                      <div 
                        className={`
                          absolute inset-0 bg-gradient-to-r from-rose-500 to-rose-600
                          transition-transform duration-500 ease-in-out
                          ${noFishConsumed ? 'translate-x-0' : '-translate-x-full'}
                        `}
                      />
                      
                      {/* Content */}
                      <div className="relative flex items-center gap-3">
                        {/* Custom checkbox with animated no-entry icon */}
                        <div className="relative flex-shrink-0">
                          <input
                            id="no-fish-toggle"
                            type="checkbox"
                            checked={noFishConsumed}
                            onChange={toggleNoFishConsumed}
                            disabled={isSaving}
                            className="sr-only"
                          />
                          <div 
                            className={`
                              w-8 h-8 rounded-full flex items-center justify-center
                              transition-all duration-300
                              ${noFishConsumed 
                                ? 'bg-white scale-110' 
                                : 'bg-white border-2 border-gray-400 group-hover:border-gray-500'
                              }
                            `}
                          >
                            {noFishConsumed ? (
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-6 w-6 text-rose-600" 
                                viewBox="0 0 24 24" 
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                {/* Circle */}
                                <circle 
                                  cx="12" 
                                  cy="12" 
                                  r="10"
                                  className="animate-in zoom-in duration-200"
                                />
                                {/* Diagonal line with draw animation */}
                                <line 
                                  x1="4.93" 
                                  y1="4.93" 
                                  x2="19.07" 
                                  y2="19.07"
                                  className="origin-top-left"
                                  style={{
                                    animation: 'drawLine 0.4s ease-out 0.1s forwards',
                                    strokeDasharray: '20',
                                    strokeDashoffset: '20'
                                  }}
                                />
                              </svg>
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-gray-200 group-hover:bg-gray-300" />
                            )}
                          </div>
                        </div>
                        
                        {/* Text */}
                        <div className="flex items-center gap-2">
                          <span 
                            className={`
                              text-lg font-medium transition-colors duration-300
                              ${noFishConsumed ? 'text-white' : 'text-gray-700'}
                            `}
                          >
                            {t("logHistoryDateLog_NoFishConsumed")}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Add CSS animation for line drawing */}
                    <style>{`
                      @keyframes drawLine {
                        to {
                          stroke-dashoffset: 0;
                        }
                      }
                    `}</style>
                  </label>
                </div>
              </div>
            )}
          </div>

          {!noFishConsumed && (
            <button
              onClick={() => addNewMealRecord()}
              className="button-base !text-xl button-bg-grad-green flex items-center justify-center gap-2 my-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
              <span>{t("logHistoryDateLog_AddNewMeal")}</span>
            </button>
          )}
        </div>

        <div className="bottom-nav-bar w-full flex justify-center sticky bottom-0 px-scr py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
          <button
            onClick={backInfo.action}
            disabled={isSaving}
            className={`button-base !text-lg flex items-center justify-center gap-2 ${
              isSaving 
                ? 'bg-gray-400 cursor-not-allowed opacity-60' 
                : 'button-bg-grad-2'
            }`}
          >
            {isSaving ? (
              <>
                <svg 
                  className="animate-spin h-5 w-5 text-white" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{t("saving") || "Saving..."}</span>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                  />
                </svg>
                <span>{t(backInfo.text)}</span>
              </>
            )}
          </button>
        </div>
      </div>
      {modalChildren && (
        <ModalWindow isOpen={!!modalChildren} onClose={modalCloseHandler}>
          {modalChildren}
        </ModalWindow>
      )}
    </motion.div>
  );
}
